/**
 * Sepia — warm paper-like theme for low-eye-strain work. Cream
 * background, brown ink, serif-leaning headings. The same component
 * primitives, just dressed differently.
 */
import { createTheme, alpha } from '@mui/material/styles';

const S = {
  primary: '#9A5B2A',
  primaryDark: '#6B3D17',
  primaryLight: '#E8D6BD',
  bg: '#F5EFE2',
  surface: '#FBF6E9',
  surfaceAlt: '#F1E9D3',
  surfaceHover: '#EFE6D0',
  border: '#D6C9A8',
  borderLight: '#E6DCC4',
  text: '#3B2F1E',
  textSecondary: '#7B6647',
};

const sharedSepia = {
  typography: {
    fontFamily: '"Frank Ruhl Libre", "Georgia", "Rubik", serif',
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700, letterSpacing: '-0.015em' },
    h5: { fontWeight: 700, letterSpacing: '-0.015em' },
    h6: { fontWeight: 600, fontSize: '1.05rem' },
    subtitle1: { fontWeight: 600, fontSize: '0.95rem' },
    subtitle2: { fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
    body1: { fontSize: '0.9rem', lineHeight: 1.75 },
    body2: { fontSize: '0.825rem', lineHeight: 1.65 },
    caption: { fontSize: '0.75rem' },
  },
  palette: {
    primary: { main: S.primary, dark: S.primaryDark, light: S.primaryLight },
    background: { default: S.bg, paper: S.surface },
    text: { primary: S.text, secondary: S.textSecondary },
    divider: S.border,
  },
  shape: { borderRadius: 8 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: S.bg,
          backgroundImage: 'linear-gradient(180deg, #F5EFE2 0%, #EBE2CB 100%)',
          backgroundAttachment: 'fixed',
        },
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        '*::-webkit-scrollbar-thumb': { backgroundColor: '#C9B894', borderRadius: 4 },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 8, fontWeight: 600, fontSize: '0.875rem',
          textTransform: 'none' as const, padding: '8px 18px',
        },
        outlined: {
          borderColor: S.border, color: S.text,
          '&:hover': { backgroundColor: S.surfaceHover, borderColor: S.primary },
        },
        text: { '&:hover': { backgroundColor: alpha(S.primary, 0.08) } },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' as const },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8, fontSize: '0.875rem', backgroundColor: S.surface,
            '& fieldset': { borderColor: S.border },
            '&:hover fieldset': { borderColor: S.primary },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: `1px solid ${S.border}`,
          backgroundColor: S.surface,
          boxShadow: '0 1px 3px 0 rgba(120,90,40,0.10)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: 10, backgroundImage: 'none' } },
    },
    MuiAppBar: {
      defaultProps: { color: 'inherit' as const, elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: S.surface,
          color: S.text,
          borderBottom: `1px solid ${S.border}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            fontWeight: 700, color: S.textSecondary,
            backgroundColor: S.surfaceAlt, borderBottom: `1px solid ${S.border}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { fontSize: '0.875rem', borderColor: S.borderLight, padding: '12px 16px' },
      },
    },
    MuiTableRow: {
      styleOverrides: { root: { '&:hover': { backgroundColor: S.surfaceHover } } },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: { fontSize: '0.875rem', '&:hover': { backgroundColor: S.surfaceHover } },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem', fontWeight: 600, background: S.text,
          padding: '6px 10px', borderRadius: 6,
        },
      },
    },
  },
};

export const sepiaRtlTheme = createTheme({ ...sharedSepia, direction: 'rtl' });
export const sepiaLtrTheme = createTheme({ ...sharedSepia, direction: 'ltr' });

export const SEPIA_SIDEBAR_COLORS = {
  background:      S.surface,
  backgroundLight: S.surfaceAlt,
  text:            S.text,
  textSecondary:   S.textSecondary,
  activeItem:      alpha(S.primary, 0.12),
  hoverItem:       S.surfaceHover,
  activeText:      S.primary,
  border:          S.border,
};
